import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * useSearchParams
 *
 * Manage a defined set of URL search params with easy toggling/loading behavior.
 *
 * Signature:
 *   useSearchParams(definedOptions, parentReplace = true)
 *
 * definedOptions can be:
 *   - an array of keys (all treated as boolean flags, default false)
 *   - an object mapping keys -> defaultValue (boolean or string)
 *   - an object mapping keys -> schema object:
 *       {
 *         default: any,                     // required default/initial value
 *         validate?: (value, params) => boolean, // optional per-key validator
 *         onInvalid?: 'revert'|'default'|'null'  // how to handle failed validation (default: 'revert')
 *       }
 *
 * parentReplace sets the default replace behavior for URL updates (can be
 * overridden per-call by passing { replace, navigate } to setOption/toggleOption/clearOptions/setOptions).
 *
 * Returned API:
 *   {
 *     params,          // current params object (keys from definedOptions)
 *     loading,         // boolean: true while syncing URL (one animation frame)
 *     setOption(key, value, opts),
 *     setOptions(objOfKeyValues, opts), // set multiple params at once
 *     toggleOption(key, opts),
 *     isActive(key),   // boolean helper (for boolean params)
 *     clearOptions(opts),  // reset to defaults and update URL
 *
 *     // Notes on validation:
 *     // - If a validator returns false, the value is resolved based on onInvalid:
 *     //   'revert'  -> keep previous value (no change)
 *     //   'default' -> use schema default
 *     //   'null'    -> set to null (removed from URL)
 *     // - URL parsing also applies validation. If 'revert' has no previous value (e.g., initial load),
 *     //   it falls back to the default.
 *   }
 */
export default function useSearchParams(
    definedOptions = [],
    parentReplace = true
) {
    const isClient = typeof window !== "undefined" && !!window.location;

    // react-router navigate (default navigation). Note: this hook requires a Router context.
    const rrNavigate = useNavigate();

    // normalize defined options into a stable schema and defaults
    // schema: { [key]: { default, validate?: fn, onInvalid?: 'revert'|'default'|'null' } }
    const schema = useRef(
        (() => {
            const makeSchemaEntry = (val) => {
                if (val && typeof val === "object" && !Array.isArray(val)) {
                    const hasSchemaKeys =
                        Object.prototype.hasOwnProperty.call(val, "default") ||
                        Object.prototype.hasOwnProperty.call(val, "validate") ||
                        Object.prototype.hasOwnProperty.call(val, "onInvalid");
                    if (hasSchemaKeys) {
                        return {
                            default: val.default,
                            validate:
                                typeof val.validate === "function"
                                    ? val.validate
                                    : undefined,
                            onInvalid:
                                val.onInvalid === "default" ||
                                val.onInvalid === "null"
                                    ? val.onInvalid
                                    : "revert",
                        };
                    }
                }
                // treat as plain default
                return {
                    default: val,
                    validate: undefined,
                    onInvalid: "revert",
                };
            };

            let normalized = {};
            if (Array.isArray(definedOptions)) {
                normalized = definedOptions.reduce((acc, k) => {
                    acc[k] = {
                        default: false,
                        validate: undefined,
                        onInvalid: "revert",
                    };
                    return acc;
                }, {});
            } else if (definedOptions && typeof definedOptions === "object") {
                normalized = Object.keys(definedOptions).reduce((acc, k) => {
                    acc[k] = makeSchemaEntry(definedOptions[k]);
                    return acc;
                }, {});
            }
            return normalized;
        })()
    ).current;

    const defaults = useRef(
        Object.keys(schema).reduce((acc, k) => {
            acc[k] = schema[k].default;
            return acc;
        }, {})
    ).current;

    const resolveInvalid = useCallback(
        (key, proposedValue, prevParams) => {
            const { validate, onInvalid, default: def } = schema[key] || {};
            if (typeof validate === "function") {
                const ok = validate(proposedValue, prevParams);
                if (!ok) {
                    if (onInvalid === "default") return def;
                    if (onInvalid === "null") return null;
                    // revert behavior
                    if (
                        prevParams &&
                        Object.prototype.hasOwnProperty.call(prevParams, key)
                    ) {
                        return prevParams[key];
                    }
                    // no prev available (e.g., initial parse): fallback to default
                    return def;
                }
            }
            return proposedValue;
        },
        [schema]
    );

    const parseFromURL = useCallback(() => {
        if (!isClient) return { ...defaults };
        const sp = new URLSearchParams(window.location.search);
        const parsed = {};
        for (const key of Object.keys(defaults)) {
            const def = defaults[key];
            if (typeof def === "boolean") {
                // treat presence (and non '0') as true
                if (!sp.has(key)) {
                    parsed[key] = def;
                } else {
                    parsed[key] = sp.get(key) !== "0";
                }
            } else {
                const val = sp.get(key);
                parsed[key] = val != null ? val : def;
            }
        }
        // apply validation without previous context (will fallback to default on 'revert')
        const sanitized = {};
        for (const key of Object.keys(parsed)) {
            sanitized[key] = resolveInvalid(key, parsed[key], undefined);
        }
        return sanitized;
    }, [defaults, isClient, resolveInvalid]);

    const [params, setParams] = useState(() => parseFromURL());
    const [loading, setLoading] = useState(false);

    // helper to build URLSearchParams from params but only include keys that differ from defaults
    const buildSearch = useCallback(
        (nextParams) => {
            const sp = new URLSearchParams();
            for (const key of Object.keys(defaults)) {
                const def = defaults[key];
                const val = nextParams[key];
                if (typeof def === "boolean") {
                    // include only if different from default and true
                    if (val === null || val === undefined) {
                        // explicit null/undefined -> omit from URL
                    } else if (val !== def && val === true) {
                        sp.set(key, "1");
                    } else if (val !== def && val === false) {
                        // if default is true and we turned it off, include explicit 0
                        // otherwise don't include it
                        if (def === true) sp.set(key, "0");
                    }
                } else {
                    if (val != null && val !== def && String(val) !== "") {
                        sp.set(key, String(val));
                    }
                }
            }
            return sp.toString();
        },
        [defaults]
    );

    // update the URL and local state
    const syncURL = useCallback(
        (
            nextParams,
            { replace = parentReplace, navigate = undefined } = {}
        ) => {
            if (!isClient) {
                setParams(nextParams);
                return;
            }
            setLoading(true);
            const newSearch = buildSearch(nextParams);
            const newUrl =
                window.location.pathname +
                (newSearch ? `?${newSearch}` : "") +
                window.location.hash;

            try {
                // prefer explicit navigate override, then react-router navigate, then history API fallback
                if (typeof navigate === "function") {
                    navigate(newUrl, { replace });
                } else if (typeof rrNavigate === "function") {
                    rrNavigate(newUrl, { replace });
                } else {
                    if (replace) {
                        window.history.replaceState({}, "", newUrl);
                    } else {
                        window.history.pushState({}, "", newUrl);
                    }
                }
            } catch {
                // fall back to history API no-op if navigation restricted or react-router not available
                try {
                    if (replace) {
                        window.history.replaceState({}, "", newUrl);
                    } else {
                        window.history.pushState({}, "", newUrl);
                    }
                } catch {
                    // ignore
                }
            }

            setParams(nextParams);
            // clear loading next animation frame so callers can show brief "saving" state if desired
            window.requestAnimationFrame(() => setLoading(false));
        },
        [buildSearch, isClient, parentReplace, rrNavigate]
    );

    const setOption = useCallback(
        (key, value, opts = {}) => {
            if (!(key in defaults)) {
                throw new Error(`useSearchParams: key "${key}" is not defined`);
            }
            const resolved = resolveInvalid(key, value, params);
            if (resolved === params[key]) {
                // no effective change after validation -> do nothing
                return;
            }
            const next = { ...params, [key]: resolved };
            syncURL(next, opts);
        },
        [defaults, params, resolveInvalid, syncURL]
    );

    // set multiple options at once
    const setOptions = useCallback(
        (nextValues = {}, opts = {}) => {
            const unknown = Object.keys(nextValues).find(
                (k) => !(k in defaults)
            );
            if (unknown) {
                throw new Error(
                    `useSearchParams: key "${unknown}" is not defined`
                );
            }
            const draft = { ...params };
            let changed = false;
            for (const k of Object.keys(nextValues)) {
                const resolved = resolveInvalid(k, nextValues[k], params);
                if (draft[k] !== resolved) {
                    draft[k] = resolved;
                    changed = true;
                }
            }
            if (changed) syncURL(draft, opts);
        },
        [defaults, params, resolveInvalid, syncURL]
    );

    const toggleOption = useCallback(
        (key, opts = {}) => {
            if (!(key in defaults)) {
                throw new Error(`useSearchParams: key "${key}" is not defined`);
            }
            const cur = params[key];
            if (typeof defaults[key] !== "boolean") {
                // non-boolean: toggle between default and empty string
                const proposed = cur === defaults[key] ? "" : defaults[key];
                setOption(key, proposed, opts);
            } else {
                setOption(key, !cur, opts);
            }
        },
        [defaults, params, setOption]
    );

    const isActive = useCallback(
        (key) => {
            if (!(key in defaults)) {
                return false;
            }
            const def = defaults[key];
            const val = params[key];
            if (typeof def === "boolean") return !!val;
            return val != null && val !== "" && val !== def;
        },
        [defaults, params]
    );

    const clearOptions = useCallback(
        (opts = {}) => {
            syncURL({ ...defaults }, opts);
        },
        [defaults, syncURL]
    );

    // listen to back/forward and sync when popstate occurs
    useEffect(() => {
        if (!isClient) return;
        const onPop = () => {
            const parsed = parseFromURL();
            // apply validation with previous params to honor 'revert' when possible
            const sanitized = { ...parsed };
            for (const key of Object.keys(defaults)) {
                sanitized[key] = resolveInvalid(key, parsed[key], params);
            }
            setParams(sanitized);
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, [isClient, parseFromURL, resolveInvalid, defaults, params]);

    // keep local state in sync if the URL changes externally (rare)
    useEffect(() => {
        if (!isClient) return;
        const observer = new MutationObserver(() => {
            // noop, but keep for potential extensions
        });
        // safe no-op, simply ensure initial sync
        const current = parseFromURL();
        // only update if different
        const changed = Object.keys(defaults).some(
            (k) => current[k] !== params[k]
        );
        if (changed) setParams(current);
        return () => observer.disconnect();
    }, [defaults, isClient, parseFromURL, params]);

    return {
        params,
        loading,
        setOption,
        setOptions,
        toggleOption,
        isActive,
        clearOptions,
    };
}
